// The "Need assistance?" customer-service block the auth pages append (legacy
// cms/blocks/customer-service-info-block.html). All copy arrives pre-localized
// as props (server-side gettext), keeping the legacy {% trans %} localization
// semantics under whatever LANGUAGES the site configures -- a ttag literal here
// would bypass the catalog entirely. The chat links reuse the shared ChatLink
// (online -> chat widget, offline -> the help-center link); the badge icons
// render as plain lazy <img> tags with empty (decorative) alt text, and the
// phone number renders as a plain anchor -- both matching stearns' legacy
// partial (sealy's block wrapped the phone in an <h3> and gave the badges
// non-empty alts; stearns did neither).
import { Immutable } from "immer";
import React from "react";
import { ChatLink } from "tsi-common-react/src/components/ChatLink/index";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type IProps = Immutable<{
    info: NonNullable<templates.LoginDocumentTemplate["cs_info"]>;
}>;

export const CustomerServiceInfoBlock = ({
    info,
}: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const offlineLink = isoWebPageURL.wrap(info.chat_offline_link);
    return (
        <section
            // "customer-service-info-block" is kept as a hook for server/cms/tests (*_documents.py)
            className={concatClassNames([
                styles.customerServiceInfoBlock,
                "customer-service-info-block",
            ])}
        >
            <header>
                <h2>{info.heading}</h2>
                <div className={styles.combinedCopy}>
                    {info.intro_lead}{" "}
                    <ChatLink
                        className=""
                        chatOnlineText="Chat"
                        chatOfflineText="Contact us"
                        chatOfflineLink={offlineLink}
                        hideIfOffline={false}
                    />{" "}
                    {info.intro_tail}
                </div>
            </header>

            <div
                // "customer-service-info-block__card" is kept as a hook for server/cms/tests (*_documents.py)
                className={concatClassNames([
                    styles.customerServiceInfoBlockCard,
                    "customer-service-info-block__card",
                ])}
            >
                <img
                    src={`${staticURL}img/icons/badge-headset.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>
                    <strong>{info.call_title}</strong>
                </h3>
                <p>{info.call_copy}</p>
                <a href={info.phone_href}>{info.phone_display}</a>
                <div className="customer-service-info-block__hours u-list-unstyled">
                    {info.hours}
                </div>
            </div>

            <div
                // "customer-service-info-block__card" is kept as a hook for server/cms/tests (*_documents.py)
                className={concatClassNames([
                    styles.customerServiceInfoBlockCard,
                    "customer-service-info-block__card",
                ])}
            >
                <img
                    src={`${staticURL}img/icons/badge-sleep-expert.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>
                    <strong>{info.chat_title}</strong>
                </h3>
                <p>{info.chat_copy}</p>
                <ChatLink
                    className="chat-link"
                    chatOnlineText="Chat"
                    chatOfflineText="Contact us"
                    chatOfflineLink={offlineLink}
                    hideIfOffline={false}
                />
                <div className="customer-service-info-block__hours u-list-unstyled">
                    {info.hours}
                </div>
            </div>
        </section>
    );
};
